import { useMemo, useState } from 'react';
import {
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Stack,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import {
    Alert,
    AlertDescription,
    AlertIcon,
    Button,
    Card,
    Heading,
    LoadingIndicator,
} from '@/components/v4';
import { breadCrumbStyling } from 'components/forms/new/formStyles';
import { useQuery } from 'hooks/useCustomQuery';
import getEightByEightCatalogue from 'API/ServiceProfiles/getEightByEightCatalogue';
import updateEightByEightService from 'API/Service/updateEightByEightService';
import {
    buildEightByEightServiceCatalogItems,
    initFromService,
} from 'pages/services/new/edit/eightByEightLicenceMapping';
import LicenceEntitlementsTree from './LicenceEntitlementsTree';

export default function LicenceEntitlements({ serviceWizard }) {
    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany } = useSelector((state) => state.navigationLists);
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // The 8x8 service created on the previous (Company Details) screen.
    const service = serviceWizard.eightByEightCompanyDetails?.service;
    const serviceID = service?.id;
    const companyID =
        typeof currentCompany === 'object'
            ? currentCompany?.id
            : currentCompany;

    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [entitlements, setEntitlements] = useState(null);

    const { data: catalogue = [], isLoading: catalogueLoading } = useQuery({
        queryKey: ['8x8CompanyAvailableLicences', companyID],
        queryFn: async () => {
            const res = await getEightByEightCatalogue({
                level: 'company',
                companyID,
            });
            return res.data || [];
        },
        enabled: !!serviceID && !!companyID,
        refetchOnWindowFocus: false,
    });

    const resolvedEntitlements = useMemo(() => {
        if (entitlements !== null) return entitlements;
        if (catalogue.length) {
            return initFromService(catalogue, []);
        }
        return [];
    }, [entitlements, catalogue]);

    const isLoading = catalogueLoading;

    const handleBack = () => serviceWizard.setFlow('AddEightByEight');

    const handleSave = async () => {
        setSaveError(null);
        setIsSaving(true);
        try {
            const catalogItems = buildEightByEightServiceCatalogItems({
                serviceId: serviceID,
                entitlements: resolvedEntitlements,
                serviceCatalogItems: service?.catalogItems,
                catalogue,
            });

            await updateEightByEightService({
                id: serviceID,
                name: service?.name,
                catalogItems,
            });

            // Success — invalidate services query, reset wizard, navigate away
            queryClient.invalidateQueries([
                'services',
                `company: ${currentCompany}`,
            ]);
            serviceWizard.reset();
            navigate('/dashboard-services');
        } catch (e) {
            setSaveError(
                e?.response?.data?.message ||
                    'Failed to save licences. Please try again.',
            );
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) return <LoadingIndicator />;

    return (
        <Box maxWidth="1064px" position="relative">
            <Breadcrumb sx={breadCrumbStyling}>
                <BreadcrumbItem>
                    <BreadcrumbLink
                        onClick={() => serviceWizard.setFlow('Start')}>
                        Dashboard
                    </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem>
                    <BreadcrumbLink
                        onClick={() => serviceWizard.setFlow('CreateService')}>
                        Add New Service
                    </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem>
                    <BreadcrumbLink
                        onClick={() =>
                            serviceWizard.setFlow('AddEightByEight')
                        }>
                        8×8
                    </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem isCurrentPage>
                    <BreadcrumbLink>Licence Entitlements</BreadcrumbLink>
                </BreadcrumbItem>
            </Breadcrumb>

            <Card
                borderRadius={15}
                p="30px"
                maxWidth="895px"
                ml={0}
                mt={2}
                bg={darkMode ? 'dark.tcap' : null}>
                <Heading
                    as="h3"
                    size="md"
                    mb={4}
                    color={darkMode ? 'white' : 'gray.700'}>
                    Licence Entitlements
                </Heading>

                {saveError ? (
                    <Alert status="error" variant="left-accent" mb={6}>
                        <AlertIcon />
                        <AlertDescription>{saveError}</AlertDescription>
                    </Alert>
                ) : (
                    <Alert status="info" variant="left-accent" mb={6}>
                        <AlertIcon />
                        <AlertDescription>
                            This is not a purchase order. These settings control
                            how many licences of each type are made available as
                            entitlements under this company. Actual provisioning
                            and billing are handled separately.
                        </AlertDescription>
                    </Alert>
                )}

                <LicenceEntitlementsTree
                    catalogue={catalogue}
                    value={resolvedEntitlements}
                    onChange={setEntitlements}
                    darkMode={darkMode}
                    hasVolume
                    hasActive
                    activeMap={{}}
                />
            </Card>

            <Stack direction="row" spacing={3} mt={5}>
                <Button variant="outline" onClick={handleBack}>
                    Back
                </Button>
                <Button
                    isLoading={isSaving}
                    loadingText="Saving"
                    onClick={handleSave}>
                    Save
                </Button>
            </Stack>
        </Box>
    );
}
